import { isAccessibilityAuditor } from '../accessibilityAudit';

export interface ApprovalDecision {
  id: string;
  title: string;
  requestType: string;
  action: string;
  actionBy: string;
  actionDate: string;
  submittedDate: string;
  decisionTimeHours: number | null;
}

export interface DecisionHistoryFilters {
  from: number | null;
  until: number;
  requestTypes: string[];
  actions: string[];
  actors: string[];
}

export const canReadApprovalBottlenecks = (roles: string[]) =>
  roles.some((role) => role === 'AskIAM.Admin' || role === 'AskIAM.Reports.Adoption.Read');

export const canReadApprovalDecisionHistory = (roles: string[]) =>
  isAccessibilityAuditor(roles) || roles.some((role) =>
    role === 'AskIAM.Admin' || role === 'AskIAM.MsApprove.Admin' ||
    (role.startsWith('AskIAM.Requests.') && role.endsWith('.Manage')));

export const decisionActionTime = (decision: ApprovalDecision): number | null => {
  const time = Date.parse(decision.actionDate);
  return Number.isFinite(time) && !decision.actionDate.startsWith('0001-01-01') ? time : null;
};

export const decisionElapsedHours = (decision: ApprovalDecision): number | null => {
  const actionTime = decisionActionTime(decision);
  const submittedTime = Date.parse(decision.submittedDate);
  const hours = decision.decisionTimeHours;
  return actionTime !== null && Number.isFinite(submittedTime) && actionTime >= submittedTime &&
    hours !== null && Number.isFinite(hours) && hours >= 0 ? hours : null;
};

export const filterDecisionHistory = (decisions: ApprovalDecision[], filters: DecisionHistoryFilters) => {
  const matching = decisions.filter((decision) =>
    (filters.requestTypes.length === 0 || filters.requestTypes.includes(decision.requestType)) &&
    (filters.actions.length === 0 || filters.actions.includes(decision.action)) &&
    (filters.actors.length === 0 || filters.actors.includes(decision.actionBy)));
  const rows: ApprovalDecision[] = [];
  let undatedCount = 0;
  let outsideWindowCount = 0;
  for (const decision of matching) {
    const time = decisionActionTime(decision);
    if (time === null) {
      undatedCount++;
    } else if ((filters.from !== null && time < filters.from) || time >= filters.until) {
      outsideWindowCount++;
    } else {
      rows.push(decision);
    }
  }
  rows.sort((a, b) => Date.parse(b.actionDate) - Date.parse(a.actionDate));
  return { rows, undatedCount, outsideWindowCount, sourceCount: matching.length };
};

export const summarizeDecisionHistory = (decisions: ApprovalDecision[]) => {
  const durations = decisions.map(decisionElapsedHours)
    .filter((hours): hours is number => hours !== null)
    .sort((a, b) => a - b);
  const approved = decisions.filter((decision) => /^(approve|approved)$/i.test(decision.action)).length;
  const rejected = decisions.filter((decision) => /^(reject|rejected)$/i.test(decision.action)).length;
  const denominator = approved + rejected;
  return {
    total: decisions.length,
    approved,
    rejected,
    other: decisions.length - denominator,
    approvalRate: denominator === 0 ? null : 100 * approved / denominator,
    measuredCount: durations.length,
    unmeasuredCount: decisions.length - durations.length,
    averageHours: durations.length === 0 ? null : durations.reduce((sum, hours) => sum + hours, 0) / durations.length,
    p95Hours: durations.length === 0 ? null : durations[Math.ceil(durations.length * 0.95) - 1],
  };
};

export const weeklyDecisionHistory = (decisions: ApprovalDecision[]) => {
  const weeks = new Map<string, ApprovalDecision[]>();
  for (const decision of decisions) {
    const time = decisionActionTime(decision);
    if (time === null) continue;
    const start = new Date(time);
    start.setUTCHours(0, 0, 0, 0);
    start.setUTCDate(start.getUTCDate() - start.getUTCDay());
    const week = start.toISOString().slice(0, 10);
    const rows = weeks.get(week) ?? [];
    rows.push(decision);
    weeks.set(week, rows);
  }
  return [...weeks.entries()].sort(([a], [b]) => a.localeCompare(b))
    .map(([week, rows]) => ({ week, ...summarizeDecisionHistory(rows) }));
};
